import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface Destination {
  id: number;
  name: string;
  location: string;
  description: string;
  price: string;
  features: string[];
  image: string;
}

const destinations: Destination[] = [
  {
    id: 1,
    name: 'Bear Mountain State Park',
    location: 'Bear Mountain, NY',
    description: 'Scenic mountain views with full RV hookups and extensive hiking trails. Perfect for outdoor enthusiasts seeking adventure.',
    price: '$35',
    features: ['Full Hookups', 'Hiking Trails', 'Lake Access', 'Pet Friendly'],
    image: 'https://public.readdy.ai/ai/img_res/d2bcf7c44d3792efb548b552170a9811.jpg',
  },
  {
    id: 2,
    name: 'Minnewaska State Park',
    location: 'Kerhonkson, NY',
    description: 'Pristine lakes and waterfalls perfect for family camping. Enjoy swimming, hiking, and breathtaking natural scenery.',
    price: '$42',
    features: ['Swimming', 'Waterfalls', 'Rock Climbing', 'Scenic Views'],
    image: 'https://readdy.ai/api/search-image?query=Minnewaska%20State%20Park%20camping%20with%20pristine%20lake%20view%2C%20waterfall%20in%20distance%2C%20tent%20camping%20site%20near%20water%2C%20beautiful%20natural%20landscape%2C%20simple%20background%20emphasizing%20the%20scenic%20beauty&width=800&height=600&seq=dest-minnewaska-1&orientation=landscape',
  },
  {
    id: 3,
    name: 'Lake Taghkanic State Park',
    location: 'Ancram, NY',
    description: 'Lakeside camping with swimming and boating activities. Ideal for water sports enthusiasts and families.',
    price: '$38',
    features: ['Beach Access', 'Boat Launch', 'Fishing', 'Playground'],
    image: 'https://readdy.ai/api/search-image?query=Lake%20Taghkanic%20camping%20site%20by%20the%20water%2C%20lakeside%20RV%20camping%2C%20peaceful%20lake%20setting%20with%20trees%2C%20boats%20on%20water%2C%20serene%20camping%20atmosphere%2C%20simple%20clean%20background&width=800&height=600&seq=dest-taghkanic-1&orientation=landscape',
  },
  {
    id: 4,
    name: 'Catskill Mountains',
    location: 'Catskill Region, NY',
    description: 'Remote wilderness camping for the adventurous spirit. Experience true backcountry camping in stunning mountain terrain.',
    price: '$30',
    features: ['Wilderness', 'Backcountry', 'Mountain Views', 'Wildlife'],
    image: 'https://readdy.ai/api/search-image?query=Catskill%20Mountains%20wilderness%20camping%2C%20remote%20mountain%20campsite%2C%20tent%20in%20forest%20clearing%2C%20dramatic%20mountain%20peaks%2C%20adventurous%20camping%20scene%2C%20simple%20background%20highlighting%20nature&width=800&height=600&seq=dest-catskill-1&orientation=landscape',
  },
  {
    id: 5,
    name: 'Harriman State Park',
    location: 'Stony Point, NY',
    description: 'Extensive trail network with secluded camping spots. Over 200 miles of trails for hiking and exploration.',
    price: '$40',
    features: ['200+ Miles Trails', 'Secluded Sites', 'Lakes', 'Historic Sites'],
    image: 'https://readdy.ai/api/search-image?query=Harriman%20State%20Park%20camping%20area%2C%20secluded%20forest%20campsite%2C%20hiking%20trails%20through%20woods%2C%20peaceful%20camping%20environment%2C%20tall%20trees%20and%20natural%20setting%2C%20simple%20clean%20background&width=800&height=600&seq=dest-harriman-1&orientation=landscape',
  },
  {
    id: 6,
    name: 'Taconic State Park',
    location: 'Copake Falls, NY',
    description: 'Waterfall views and premium RV facilities. Features the stunning Bash Bish Falls, the highest waterfall in Massachusetts.',
    price: '$45',
    features: ['Waterfall Views', 'Premium Sites', 'Electric Hookups', 'Showers'],
    image: 'https://readdy.ai/api/search-image?query=Taconic%20State%20Park%20RV%20camping%20with%20waterfall%20view%2C%20premium%20campground%20facilities%2C%20modern%20RV%20site%20near%20cascading%20waterfall%2C%20beautiful%20natural%20scenery%2C%20simple%20background&width=800&height=600&seq=dest-taconic-1&orientation=landscape',
  },
];

const DestinationsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24">
        <section className="py-20 bg-sage-light">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="font-serif text-6xl font-bold text-gray-900 mb-6">Camping Destinations</h1>
            <p className="text-xl text-gray-600 max-w-3xl">
              Explore the best camping and RV sites throughout the Hudson Valley. From lakeside retreats to mountain adventures, find your perfect outdoor escape.
            </p>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 gap-8">
              {destinations.map((destination) => (
                <article
                  key={destination.id}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden group cursor-pointer"
                  data-product-shop
                >
                  <div className="h-80 overflow-hidden relative">
                    <img
                      src={destination.image}
                      alt={destination.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 px-4 py-2 bg-black/60 backdrop-blur-sm rounded-full">
                      <p className="text-white text-sm font-medium whitespace-nowrap">From {destination.price}/night</p>
                    </div>
                  </div>
                  
                  <div className="p-8">
                    <div className="mb-4">
                      <h3 className="font-bold text-2xl text-gray-900 mb-2 group-hover:text-forest transition-colors">
                        {destination.name}
                      </h3>
                      <p className="text-sm text-gray-500 flex items-center gap-2">
                        <i className="ri-map-pin-line"></i>
                        {destination.location}
                      </p>
                    </div>
                    
                    <p className="text-gray-600 leading-relaxed mb-6">
                      {destination.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-2">
                      {destination.features.map((feature, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-sage text-forest text-xs font-medium rounded-full"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default DestinationsPage;